import { IBaseApiResponse } from './lead-documents-get.model'

import { ILeadDocument } from './lead-document.model'

export enum EApprovalStatus {
    SENT_FOR_APPROVAL = 'sent_for_approval',
    COMMENTED = 'commented',
    REJECTED = 'rejected',
    APPROVED = 'approved',
}

export enum EDepartment {
    FINANCE = 'Finance',
    SHIKSHA = 'Shiksha',
    OPERATIONS = 'Operations',
    LEGAL = 'Legal',
}

export interface IPocOption {
    name: string
    email: string
    department: EDepartment
    designation: string
}

export interface ILeadDocumentApprovalRow {
    id?: number
    department: string
    designation: string
    poc: string
    pocEmail?: string
    status: string
    remarks: string
    attachment: string
    attachmentUrl?: string
    attachmentFile?: File
    isSubmitting?: boolean
    error?: string
    isExisting?: boolean
    approvalId?: number
}

export interface IApprovalApprovedBy {
    user_id?: string
    name?: string
    email?: string
    department?: string
    designation?: string
    poc?: string
}

export interface IApprovalData {
    status: EApprovalStatus
    remarks: string
    approved_by?: IApprovalApprovedBy
    version_id?: number
    department?: string
    designation?: string
    poc?: string
    email?: string
}

export interface IApprovalMetadata {
    service_name?: string
    bucket?: string
    key?: string
    file_name?: string
    file_type?: string
    correlation_id?: string
    notes?: string
    custom_filename?: string
    data: IApprovalData
}

export interface IApprovalResponseData {
    id: number
    document_id: number
    version_id: number
    status: string
    remarks: string
    created_by: {
        user_id: string
        name: string
    }
    attachments: unknown[]
    created_at: string
}

export interface IApprovalResponse extends Omit<IBaseApiResponse<IApprovalResponseData>, 'data'> {
    data?: IApprovalResponseData
    missingFields?: string[]
    errors?: Record<string, string>
}

export interface IApprovalListItem {
    id: number
    document_id: number
    version_id: number
    status: EApprovalStatus
    remarks: string
    created_by: {
        user_id: string
        name: string
    }
    approved_by?: IApprovalApprovedBy
    attachments: unknown[]
    created_at: string
    updated_at?: string
}

export interface IApprovalTimelineItem {
    approval_id: number
    document_id: number
    document_type: string
    lead_id: number
    status: string
    version: string
    created_by: {
        name: string
        email: string
        user_id: string
        department: string
        designation?: string
    }
    uploaded_by?: {
        name: string
        email: string
        user_id: string
        department: string
        designation?: string
    }
    updated_by?: {
        name: string
        email: string
        user_id: string
        department: string
        designation?: string
    }
    attachments: unknown[]
    timestamp: string
    remarks: string
    is_active?: boolean
    /** Name of the uploaded proof document (when status is approved). */
    proof_document_name?: string
    /** URL of the uploaded proof document (when status is approved). */
    proof_document_url?: string
}

export interface IApprovalDocumentData {
    document_id: number
    document_type: string
    current_status: string
    timeline: IApprovalTimelineItem[]
}

export interface IApprovalsListResponse
    extends Omit<IBaseApiResponse<IApprovalDocumentData[]>, 'data'> {
    data?: IApprovalDocumentData[]
    message: string
    errors?: Record<string, string>
}

// Modal interfaces
export interface IDocumentDetailsModalData {
    document: ILeadDocument
    leadId: number
    isInactive?: boolean
    stageId?: number | null
    // Whether the parent deal is currently on hold
    isOnHold?: boolean
    hasRequestedEditsS3?: boolean
    canViewDeal?: boolean
    canCreateDeal?: boolean
    // Optional current deal status (e.g. 'dropped', 'on_hold') so the modal
    // can disable internal approvals when deal is lost/on hold.
    dealStatus?: string | null
}

/** Result when closing document details modal (parent may refresh documents / full deal). */
export interface IDocumentDetailsModalCloseResult {
    shouldRefreshDocuments?: boolean
    /** Refetch deal from API (e.g. after revised proposal upload from approvals UI). */
    refreshDealData?: boolean
    documentId?: number
    documentStatus?: string
    allApprovalsApproved?: boolean
}

export interface IReasonModalData {
    status: string
}

export interface IApprovalProofModalData {
    status: string
}

export interface IApprovalProofModalResult {
    file: File | null
}

export interface IEditApprovalAttachment {
    file_name: string
    file_type: string
    s3_url: string
    s3_common_util_id: string
    file_size?: number
}

export interface IEditApprovalRequest {
    status: EApprovalStatus
    remarks?: string
    approved_by: IApprovalApprovedBy
    attachments?: IEditApprovalAttachment[]
    uploaded_by?: IEditApprovalAttachment
}

export interface IEditApprovalResponse
    extends IBaseApiResponse<{
        timeline_id: number
        document_id: number
        status: string
    }> {}

export interface IDocumentNameModalData {
    fileName?: string
}

export interface IDocumentNameModalResult {
    documentName: string
}
